import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useAppSelector } from '../../redux/hooks';
import StyledButton from '../Common/StyledButton';
import { CALENDAR_SETTINGS_ROUTE, SUBSCRIPTION_PLANS } from '../../utils/routes';
import { ClientLimitStatus } from '../../redux/coach/coachSlice';
import { SubscriptionPlanTypes } from '../../types/coach';
import { formatPlanName, showBanner } from '../../utils/paymentUtills';

// Styled components
const StyledBannerBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100px',
  width: '100%',
  marginBottom: '10px',
  borderRadius: '5px',
  backgroundColor: '#2A3C67',
});

interface ISubscriptionBannerProps {
  isConfigError?: boolean;
}

const SubscriptionBanner = ({ isConfigError = false }: ISubscriptionBannerProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const subscriptionPlanStatus = useAppSelector(
    (state) => state.coach.coach.subscriptionPlanStatus
  );
  const loading = useAppSelector((state) => state.coach.loading);
  const { clientCountStatus, isPrincipalCoach } = useAppSelector((state) => state.coach.coach);
  const coachConfigurations = useAppSelector((state) => state.booking.coachConfigurations);
  const isCalendarError = coachConfigurations.calendar === null;
  const isVideoCallPlatformError = coachConfigurations.videoCallPlatform === null;
  const shouldShowConfigErrorBanner =
    isConfigError &&
    (isCalendarError ||
      (isVideoCallPlatformError && coachConfigurations.isProvideOnlineConsultations));
  const isArabic = i18n.language === 'ar';
  let plan = '';
  if (subscriptionPlanStatus?.currentPlan) {
    plan = formatPlanName(subscriptionPlanStatus?.currentPlan);
  }
  if (loading) return null;

  const handleActionClick = (path?: string | null) => {
    if (path) navigate(path);
  };

  const getConfigErrorBannerContent = () => {
    if (isCalendarError && isVideoCallPlatformError) {
      return {
        header: t('coachConfigurations.calendarAndVideoCallPlatformErrorTitle'),
        body: t('coachConfigurations.calendarAndVideoCallPlatformErrorDesc'),
        button: t('coachConfigurations.setupNow'),
        path: CALENDAR_SETTINGS_ROUTE,
      };
    }
    if (isCalendarError) {
      return {
        header: t('coachConfigurations.calendarErrorTitle'),
        body: t('coachConfigurations.calendarErrorDesc'),
        button: t('coachConfigurations.addCalendar'),
        path: CALENDAR_SETTINGS_ROUTE,
      };
    }
    if (isVideoCallPlatformError) {
      return {
        header: t('coachConfigurations.videoCallPlatformErrorTitle'),
        body: t('coachConfigurations.videoCallPlatformErrorDesc'),
        button: t('coachConfigurations.addVideoCallPlatform'),
        path: coachConfigurations.isProvideOnlineConsultations
          ? CALENDAR_SETTINGS_ROUTE
          : `${CALENDAR_SETTINGS_ROUTE}?tab=generalSettings`,
      };
    }
    return null;
  };

  const generateBannerMessages = (
    clientStatus: ClientLimitStatus,
    translate: TFunction<'translation', undefined>,
    paymentStatus: string,
    daysLeft?: number
  ) => {
    if (daysLeft === 0) {
      return {
        headerMessage: translate('subscriptionPlan.subscriptionEnd'),
        bodyMessage: translate('subscriptionPlan.subscriptionEndDesc'),
      };
    }

    if (daysLeft && daysLeft < 7) {
      const days = Math.max(0, daysLeft) === 0 ? t('common.today') : daysLeft;
      return {
        headerMessage: translate('subscriptionPlan.subscriptionAboutToEnd'),
        bodyMessage: translate('subscriptionPlan.aboutToEndDesc', { X: days }),
      };
    }

    if (paymentStatus === 'FAILURE') {
      let bodyMessage: string;
      let paymentFailedDesc = translate('subscriptionPlan.paymentFailedDefaultDesc');
      if (subscriptionPlanStatus?.currentPlan === SubscriptionPlanTypes.FREE) {
        paymentFailedDesc = translate('subscriptionPlan.paymentFailedDesc');
      }
      switch (clientStatus) {
        case ClientLimitStatus.ABOUT_TO_REACH:
          bodyMessage = translate('subscriptionPlan.paymentAboutToReach', { paymentFailedDesc });
          break;
        case ClientLimitStatus.LIMIT_REACHED:
          bodyMessage = translate('subscriptionPlan.paymentLimitReached', { paymentFailedDesc });
          break;
        default:
          bodyMessage = translate('subscriptionPlan.paymentNotReached', { paymentFailedDesc });
      }
      return {
        headerMessage: translate('subscriptionPlan.paymentFailed'),
        bodyMessage,
      };
    }

    switch (clientStatus) {
      case ClientLimitStatus.ABOUT_TO_REACH:
        return {
          headerMessage: translate('subscriptionPlan.aboutToReach'),
          bodyMessage: translate('subscriptionPlan.aboutToReachDesc', { plan }),
        };
      case ClientLimitStatus.LIMIT_REACHED:
        return {
          headerMessage: translate('subscriptionPlan.limitReached'),
          bodyMessage: translate('subscriptionPlan.limitReachedDesc', { plan }),
        };
      default:
        return {
          headerMessage: '',
          bodyMessage: '',
        };
    }
  };

  const getButtonText = (paymentStatus: string, translate: TFunction) => {
    if (paymentStatus !== 'FAILURE') {
      return translate('subscriptionPlan.upgrade');
    }
    return translate('subscriptionPlan.fix');
  };

  const getSubscriptionBannerContent = () => {
    if (!subscriptionPlanStatus || !clientCountStatus) return null;
    const { currentPlan, paymentStatus, daysLeft } = subscriptionPlanStatus;
    const shouldShowBanner = showBanner(clientCountStatus, paymentStatus, currentPlan, daysLeft);

    if (shouldShowBanner) {
      const messages = generateBannerMessages(clientCountStatus, t, paymentStatus, daysLeft);
      return {
        header: messages.headerMessage,
        body: messages.bodyMessage,
        button: isPrincipalCoach ? getButtonText(paymentStatus, t) : undefined,
        path: SUBSCRIPTION_PLANS,
      };
    }
    return null;
  };

  const getBannerContent = () => {
    if (shouldShowConfigErrorBanner) return getConfigErrorBannerContent();
    if (subscriptionPlanStatus && clientCountStatus) return getSubscriptionBannerContent();
    return null;
  };

  const bannerContent = getBannerContent();
  if (!bannerContent) return null;

  const { header, body, button, path } = bannerContent;

  return (
    <Box
      key={isConfigError ? 'configErrorBanner' : 'subscriptionBanner'}
      position={isConfigError ? 'relative' : 'fixed'}
      top={isConfigError ? 0 : 'auto'}
      bottom={isConfigError ? 'auto' : 0}
      sx={{
        width: isConfigError ? '100%' : { sm: '82%', md: '84%', lg: '90%', xl: '90%' },
        marginBottom: isConfigError ? '10px' : 0,
      }}
    >
      <StyledBannerBox>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          fontWeight="700"
        >
          <Box
            display="flex"
            flexDirection="row"
            p="0px 10px 0 15px"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" flexDirection="row">
              <Box paddingBottom="5px">
                <img
                  src="/assets/icons/shield-warning-red.svg"
                  width={40}
                  alt="shield-warning-red"
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                textAlign={isArabic ? 'right' : 'left'}
                p="0px 10px 5px 10px"
              >
                <Typography
                  sx={{ color: '#fff', fontSize: isSmallScreen ? 14 : 20, fontWeight: 500 }}
                >
                  {header}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#fff', fontSize: isSmallScreen ? 12 : 16 }}
                >
                  {body}
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexDirection="row"
              p="5px"
            >
              {button && (
                <StyledButton
                  paddingY={12}
                  paddingX={16}
                  fontSize={isSmallScreen ? 12 : 15}
                  onClick={() => handleActionClick(path)}
                >
                  {button}
                </StyledButton>
              )}
            </Box>
          </Box>
        </Box>
      </StyledBannerBox>
    </Box>
  );
};

export default SubscriptionBanner;
